// Project-level BOM aggregator. Sums per-cabinet cached BOM maps into
// `project.totals` rows. Previously lived as a server-side trigger
// (seed/scripts/recompute_project_totals.js); after the hierarchy moved
// into a nested Project document, the SPA recomputes on every write
// instead — atomic with the rest of the project doc, no trigger lag.
//
// Per-cabinet inputs (stamped by `deriveCabinetCache`):
//   cached.partCount               number
//   cached.sheetAreaByMaterial     {materialId: sqIn}
//   cached.edgeLftByBand           {bandId:     lft}
//   cached.hardwareByModel         {modelId:    qty}
//
// Why server-side made sense (and why client-side is fine now): per-
// cabinet sheet counts can't just be summed — `ceil(sumPerCabinet) !=
// ceil(sum)`. Batch nesting (one cabinet uses 0.3 sheets, another 0.4 →
// together 1 sheet, not 2) only composes at the PROJECT level. The same
// shape applies to edge banding minimum-roll rounding once that feature
// lands. The SPA already has the full project tree in memory at the
// moment of write, so doing it client-side is cheap.
//
// Catalog dependency: needs materials / edge_bands / hardware to map
// sheet-area → sheet-count → cost. Callers pass these from the React
// Query catalog hooks.

import type {
  Cabinet,
  EdgeBand,
  Garnitur,
  Hardware,
  Material,
  ProjectTotals,
} from "@/lib/types";

interface Catalogs {
  materials: Material[];
  edges:     EdgeBand[];
  hardware:  Hardware[];
}

function addMap(dst: Record<string, number>, src?: Record<string, number>) {
  if (!src) return;
  for (const k of Object.keys(src)) {
    const v = src[k];
    if (typeof v !== "number") continue;
    dst[k] = (dst[k] ?? 0) + v;
  }
}

export function deriveProjectTotals(
  garniturs: Garnitur[],
  cat: Catalogs,
): ProjectTotals {
  const cabinets: Cabinet[] = [];
  for (const g of garniturs ?? []) {
    for (const c of g.cabinets ?? []) cabinets.push(c);
  }

  let partCount = 0;
  const sheetByMat: Record<string, number> = {};
  const edgeByBand: Record<string, number> = {};
  const hwByModel:  Record<string, number> = {};

  for (const c of cabinets) {
    const cached = c.cached ?? {};
    if (typeof cached.partCount === "number") partCount += cached.partCount;
    addMap(sheetByMat, cached.sheetAreaByMaterial);
    addMap(edgeByBand, cached.edgeLftByBand);
    addMap(hwByModel,  cached.hardwareByModel);
  }

  // Sheet usage — ceil((sqIn × 1.15) / sheetSqIn) sheets per material.
  // 15% yield loss matches the SPA's projectBom placeholder; the real
  // packer (Phase 3) plugs in here.
  const matsById = new Map(cat.materials.map((m) => [String(m._id), m]));
  const sheetUsage: ProjectTotals["sheetUsage"] = [];
  let sheetCost = 0;
  for (const k of Object.keys(sheetByMat)) {
    const m = matsById.get(k);
    if (!m) continue;
    const sqIn = sheetByMat[k]!;
    const sheetSqIn = (m.sheetW || 0) * (m.sheetH || 0);
    const nSheets = sheetSqIn > 0 ? Math.ceil((sqIn * 1.15) / sheetSqIn) : 0;
    const unitCost = m.costPerSheet || 0;
    sheetCost += nSheets * unitCost;
    sheetUsage.push({ materialId: m._id, sheets: nSheets });
  }

  const edgesById = new Map(cat.edges.map((e) => [String(e._id), e]));
  const edgeUsage: ProjectTotals["edgeUsage"] = [];
  let edgeCost = 0;
  for (const k of Object.keys(edgeByBand)) {
    const e = edgesById.get(k);
    if (!e) continue;
    const lft = edgeByBand[k]!;
    edgeCost += lft * (e.costPerLft || 0);
    edgeUsage.push({ edgeId: e._id, lft });
  }

  const hwById = new Map(cat.hardware.map((h) => [String(h._id), h]));
  const hardwareUsage: ProjectTotals["hardwareUsage"] = [];
  let hwCost = 0;
  for (const k of Object.keys(hwByModel)) {
    const h = hwById.get(k);
    if (!h) continue;
    const qty = hwByModel[k]!;
    hwCost += qty * (h.costEach || 0);
    hardwareUsage.push({ hardwareId: h._id, qty });
  }

  return {
    partCount,
    estimatedCost: sheetCost + edgeCost + hwCost,
    sheetUsage,
    edgeUsage,
    hardwareUsage,
  };
}
